import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import {Box, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {SurveyAnswer, SurveyMediaValue} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import React, {useState} from "react";
import type {SurveyAnswerContext} from "./surveyAnswer.util";
import {surveyMediaThumbnailUrl} from "./surveyAnswerMedia.util";

const THUMBNAIL_SIZE = 96;

const SIGNATURE_WIDTH = 320;

const SIGNATURE_HEIGHT = 120;

/** The guest's ink is dark on a transparent image, so the box stays white in every theme. */
const SIGNATURE_PAPER = '#FFFFFF';

const THUMBNAIL_SX = {
    width: THUMBNAIL_SIZE,
    height: THUMBNAIL_SIZE,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'action.hover',
    transition: 'border-color 150ms ease',
    '&:hover, &:focus-visible': {borderColor: 'primary.main'},
} as const;

const Unavailable = ({label}: {label: string}) => (
    <Stack alignItems={'center'} justifyContent={'center'} gap={0.5} sx={{px: 1, textAlign: 'center'}}>
        <BrokenImageOutlinedIcon sx={{fontSize: 20, color: 'text.disabled'}}/>
        <Typography variant={'body2'} color={'text.secondary'} sx={{lineHeight: 1.3}}>
            {label}
        </Typography>
    </Stack>
);

function Thumbnail({media, ctx}: {media: SurveyMediaValue, ctx: SurveyAnswerContext}) {
    const [failed, setFailed] = useState(false);
    const src = surveyMediaThumbnailUrl(media);

    const picture = src && !failed
        ? (
            <Box
                component={'img'}
                src={src}
                alt={''}
                onError={() => setFailed(true)}
                sx={{display: 'block', width: '100%', height: '100%', objectFit: 'cover'}}
            />
        )
        : <Unavailable label={ctx.t('Image not available')}/>;

    if (!media.mediaURI) return <Box sx={THUMBNAIL_SX}>{picture}</Box>;

    return (
        <Box
            component={'a'}
            href={media.mediaURI}
            target={'_blank'}
            rel={'noopener noreferrer'}
            aria-label={ctx.t('Open full size')}
            sx={THUMBNAIL_SX}
        >
            {picture}
        </Box>
    );
}

function Signature({media, ctx}: {media: SurveyMediaValue, ctx: SurveyAnswerContext}) {
    const [failed, setFailed] = useState(false);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: SIGNATURE_WIDTH,
                height: SIGNATURE_HEIGHT,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: SIGNATURE_PAPER,
            }}
        >
            {/* The line the guest signed on, under the ink. */}
            <Box
                aria-hidden
                sx={{position: 'absolute', left: 16, right: 16, bottom: 24, borderBottom: '1px solid', borderColor: 'divider'}}
            />

            {media.mediaURI && !failed
                ? (
                    <Box
                        component={'img'}
                        src={media.mediaURI}
                        alt={ctx.t('Guest signature')}
                        onError={() => setFailed(true)}
                        sx={{position: 'relative', display: 'block', width: '100%', height: '100%', objectFit: 'contain'}}
                    />
                )
                : <Unavailable label={ctx.t('Signature not available')}/>}
        </Box>
    );
}

/**
 * What the guest uploaded or signed, drawn rather than counted: square thumbnails that open the original in a new
 * tab, then the signature in the box it was drawn in. Print, PDF and Word draw the same pieces from
 * `SurveySubmissionDocumentAnswer`.
 */
export function SurveyAnswerMedia({answer, ctx}: {answer: SurveyAnswer | undefined, ctx: SurveyAnswerContext}) {
    const uploads = answer?.mediaValues ?? [];
    const signature = answer?.signatureValue;

    if (!uploads.length && !signature) return null;

    return (
        <Stack gap={1.5} sx={{minWidth: 0}}>
            {!!uploads.length && (
                <Stack direction={'row'} flexWrap={'wrap'} gap={1}>
                    {uploads.map((media, index) => (
                        <Thumbnail key={media.publicId ?? media.mediaURI ?? index} media={media} ctx={ctx}/>
                    ))}
                </Stack>
            )}

            {!!signature && <Signature media={signature} ctx={ctx}/>}
        </Stack>
    );
}
